<script setup lang="ts">
import { useVModel } from '@vueuse/core'
import type { DateRangeValueType } from "@lib/zod/dateTimePropEditor";
import RangeCalendarEditor from './RangeCalendarEditor.vue';
import { Button } from '@/components/ui/button'
import { Plus, Trash2 } from 'lucide-vue-next'

const DAY_MS = 1000 * 60 * 60 * 24

const props = defineProps<{
    modelValue?: DateRangeValueType[] | null
}>()

const emits = defineEmits<{
    (e: 'update:modelValue', payload: DateRangeValueType[]): void
}>()

const modelValue = useVModel(props, 'modelValue', emits, {
    passive: true,
    defaultValue: [],
    deep: true,
})

const newRange = (days: number): DateRangeValueType => {
    const start = Date.now()
    return {
        start,
        end: start + DAY_MS * days,
    }
}

const addRange = (days = 1) => {
    const next = [...(modelValue.value ?? []), newRange(days)]
    modelValue.value = next
}

const removeRange = (index: number) => {
    const next = [...(modelValue.value ?? [])]
    next.splice(index, 1)
    modelValue.value = next
}
</script>

<template>
    <div class="space-y-3">
        <div class="flex flex-wrap gap-2">
            <Button type="button"
                    variant="outline"
                    size="sm"
                    @click="addRange(1)">
                24 hours
            </Button>
            <Button type="button"
                    variant="outline"
                    size="sm"
                    @click="addRange(2)">
                48 hours
            </Button>
            <Button type="button"
                    variant="outline"
                    size="sm"
                    @click="addRange(7)">
                1 week
            </Button>
            <Button type="button"
                    variant="outline"
                    size="sm"
                    @click="addRange(1)">
                <Plus class="h-4 w-4" />
                Add range
            </Button>
        </div>

        <div v-for="(_range, index) in modelValue"
             :key="index"
             class="flex flex-col gap-2 rounded-md border p-3">
            <div class="flex items-center justify-between">
                <span class="text-sm font-medium">Range {{ index + 1 }}</span>
                <Button type="button"
                        variant="ghost"
                        size="sm"
                        @click="removeRange(index)">
                    <Trash2 class="h-4 w-4" />
                </Button>
            </div>
            <RangeCalendarEditor v-if="modelValue"
                                 v-model="modelValue[index]" />
        </div>
    </div>
</template>
